import {sortArray} from "./DataUtils";
import {ACCREDITATION_STATUS} from "../domain/AccreditationStatus";
import {FOLDER_TYPE, NODE_LEVEL} from "../domain/HierarchyConstants";
import {CONTENT_OFFSET} from "../domain/ContentOffset";
import {
    buildContentFolders,
    buildRowObject,
    buildServiceTypeHierarchyMap,
    buildServiceTypesHierarchy,
    flattenHierarchy
} from "./ContentHierarchyDataHelper";

export const fetchOriginalIdsForNode = (hierarchyArray, id) => {
    const result = [];

    if (hierarchyArray === undefined) {
        return result;
    }

    hierarchyArray.forEach(currentNode => {
        if (currentNode.id === id) {
            result.push(...fetchOriginalIdsFromChildren(currentNode.children));
        } else if (currentNode.children) {
            result.push(...fetchOriginalIdsForNode(currentNode.children, id));
        }
    });

    return result;
}

const fetchOriginalIdsFromChildren = (children) => {
    let result = [];

    for (let i = 0; i < children.length; i++) {
        if (children[i].originalId !== undefined) {
            result.push(children[i].originalId);
        }

        if (children[i].children) {
            result = result.concat(fetchOriginalIdsFromChildren(children[i].children));
        }
    }

    return result;
}

const buildAccreditationStatus = (currentAccreditationHierarchy) => {
    const accreditationCountForHierarchy = currentAccreditationHierarchy.reduce((count, accreditation) => count + (accreditation ? 1 : 0), 0);
    const {NO_ACCREDITATION, PARTIAL_ACCREDITATION, FULL_ACCREDITATION} = ACCREDITATION_STATUS;

    let accreditationStatus = NO_ACCREDITATION;

    if (accreditationCountForHierarchy === currentAccreditationHierarchy.length) {
        accreditationStatus = FULL_ACCREDITATION;
    } else if (accreditationCountForHierarchy > 0) {
        accreditationStatus = PARTIAL_ACCREDITATION;
    }

    return accreditationStatus;
}

export default function buildContentAccreditationHierarchy(data) {

    const contentInfos = data.contentInfos.map(item => {
        const parentServiceType = data.serviceTypes.find((serviceType) => serviceType.id === item.serviceType.id);

        const contentInfoOffsetId = item.id + (item.serviceType.id * CONTENT_OFFSET.CONTENT_INFO_ID_OFFSET);
        const contentInfoParentId = item.contentFolderId
              ? item.contentFolderId + (item.serviceType.id * CONTENT_OFFSET.CONTENT_FOLDER_ID_OFFSET)
              : item.serviceType.id;

        return {
            id: contentInfoOffsetId,
            originalId: item.id,
            text: item.name,
            parent: contentInfoParentId,
            type: NODE_LEVEL.CHILD,
            primaryColor: parentServiceType ? parentServiceType.primaryColor : null,
            parentServiceTypeId: parentServiceType ? parentServiceType.id : null,
            folderType: FOLDER_TYPE.CONTENT_INFO
        }
    });

    const contentFolders = buildContentFolders(data, contentInfos);
    const serviceTypes = buildServiceTypesHierarchy(data, contentFolders, contentInfos);
    const serviceTypeHierarchyMap = buildServiceTypeHierarchyMap(serviceTypes, contentFolders, contentInfos);

    const flattenedHierarchy = serviceTypeHierarchyMap.reduce((accreditation, serviceType) => {
        const flattenedServiceType = flattenHierarchy(serviceType);
        return [...accreditation, ...flattenedServiceType];
    }, []);

    const contentInfoAccreditationMap = [];
    data.contentPerformingAccreditations.forEach(accreditation => {
        const parent = contentInfos.find(info => info.originalId === accreditation.contentInfo.id);

        if (parent) {
            contentInfoAccreditationMap.push({
                "contentInfoId": accreditation.contentInfo.id,
                "userId": accreditation.domainUser.id,
                "parentId": parent.parent,
                "serviceTypeId": parent.parentServiceTypeId
            });
        }
    });

    const contentFolderAccreditationMap = contentFolders.map(contentFolder => {
        const additionalPropertiesForRow = {serviceTypeId: contentFolder.parentServiceTypeId};
        const folderRow = buildRowObject(flattenedHierarchy, contentFolder, additionalPropertiesForRow);
        const childrenIds = fetchOriginalIdsForNode(serviceTypeHierarchyMap.filter((data) => data.id === contentFolder.parentServiceTypeId), contentFolder.id);

        data.users_filtered.forEach(user => {
            const foundAccreditation = [];
            childrenIds.forEach(childId => {
                const accreditation = contentInfoAccreditationMap.find(value => value.contentInfoId === childId
                  && value.userId === user.id
                  && value.serviceTypeId === contentFolder.parentServiceTypeId);
                foundAccreditation.push(accreditation !== undefined);
            });
            folderRow[user.id] = buildAccreditationStatus(foundAccreditation);
        });

        return folderRow;
    });

    const serviceTypeAccreditationMap = serviceTypes.map(serviceType => {
        const additionalPropertiesForRow = {serviceTypeId: serviceType.parentServiceTypeId};
        const serviceRow = buildRowObject(flattenedHierarchy, serviceType, additionalPropertiesForRow);
        const childrenIds = fetchOriginalIdsForNode(serviceTypeHierarchyMap.filter((data) => data.id === serviceType.parentServiceTypeId), serviceType.id);

        data.users_filtered.forEach(user => {
            let foundAccreditation = [];
            childrenIds.forEach(childId => {
                let accreditation = contentInfoAccreditationMap.find(value => value.contentInfoId === childId
                  && value.userId === user.id
                  && value.serviceTypeId === serviceType.parentServiceTypeId);
                foundAccreditation.push(accreditation !== undefined);
            });

            serviceRow[user.id] = buildAccreditationStatus(foundAccreditation);
        });

        return serviceRow;
    });

    const contentInfoAccreditationStatusMap = contentInfos.map(contentInfo => {
        const additionalPropertiesForRow = {serviceTypeId: contentInfo.parentServiceTypeId};
        const contentRow = buildRowObject(flattenedHierarchy, contentInfo, additionalPropertiesForRow);

        data.users_filtered.forEach(user => {
            const foundAccreditation = data.contentPerformingAccreditations.find(accreditation => accreditation.contentInfo.id === contentInfo.originalId
              && accreditation.domainUser.id === user.id);
            contentRow[user.id] = foundAccreditation != null ? ACCREDITATION_STATUS.FULL_ACCREDITATION : ACCREDITATION_STATUS.NO_ACCREDITATION;
        });

        return contentRow;
    });

    const sortedContentInfoAccreditationMap = sortArray(contentInfoAccreditationStatusMap, 'contentNameHierarchy');
    const sortedContentFolderAccreditationMap = sortArray(contentFolderAccreditationMap, 'contentNameHierarchy');

    const accreditationHierarchy = [...serviceTypeAccreditationMap, ...sortedContentFolderAccreditationMap, ...sortedContentInfoAccreditationMap];

    return {accreditationHierarchy, serviceTypeHierarchyMap, contentInfoAccreditationMap};
}
